/**
 * Phase 4 — the reveal (SPEC §2.3, §5.2; DESIGN.md §3.6).
 *
 * The completed ranking *is* the explanation screen, so this route and
 * `JustificationScreen` render the same surface: status, heading, twelve rows,
 * rating bar, End turn. What this one does differently is settle the rows in off
 * the server's `reveal.startAt`, so the room lands together (SPEC §5.2), and
 * hold the payload for the route that follows it.
 *
 * NO leaderboard here — that is end-of-round only (SPEC §2.4). The cut to
 * justification is the server's phase change, never a timer on this client.
 */
import { useEffect, useMemo, useState } from 'react';
import { Screen, WaitingFor } from '../../components/index.js';
import {
  RevealBoard,
  RevealFooter,
  RevealHeading,
  rememberReveal,
  toLicenseMap,
  useTurnActions,
} from '../../components/reveal/index.js';
import {
  useHostControls,
  useIsHost,
  useJustificationDone,
  usePlayers,
  useReveal,
  useSelfId,
  useServerClock,
  useThumbsVote,
} from '../../store/index.js';

export function RevealScreen() {
  const reveal = useReveal();
  const players = usePlayers();
  const selfId = useSelfId();
  const { offsetMs } = useServerClock();
  const isHost = useIsHost();
  const { forceAdvance } = useHostControls();
  const sendEndTurn = useJustificationDone();
  const sendVote = useThumbsVote();
  const [advanced, setAdvanced] = useState(false);

  // Held for the justification route, which the store nulls `reveal` on the way
  // into — see `turnMemory`.
  useEffect(() => {
    if (reveal) rememberReveal(reveal);
  }, [reveal]);

  // `live: false` — the server refuses both intents while the reveal is
  // running, so a press here is held and sent the moment the phase lands.
  const actions = useTurnActions({
    turnIndex: reveal?.turnIndex ?? -1,
    questionNumber: reveal?.question.number ?? -1,
    live: false,
    sendVote,
    sendEndTurn,
  });

  const licensePlayers = useMemo(() => toLicenseMap(players), [players]);

  if (!reveal || !selfId) {
    return (
      <Screen testId="reveal-screen" eyebrow="Reveal">
        <WaitingFor headline="Counting the room." />
      </Screen>
    );
  }

  const isTurnPlayer = reveal.turnPlayerId === selfId;
  const turnPlayerName = players.find((p) => p.id === reveal.turnPlayerId)?.name ?? 'They';

  return (
    <Screen
      testId="reveal-screen"
      eyebrow={`Reveal · ${reveal.question.number}`}
      meta={isTurnPlayer ? 'YOUR TURN' : null}
      title={
        <RevealHeading
          question={reveal.question}
          isTurnPlayer={isTurnPlayer}
          turnPlayerName={turnPlayerName}
        />
      }
      bodyClassName="overflow-y-auto"
      footer={
        <RevealFooter
          turnPlayerName={turnPlayerName}
          isTurnPlayer={isTurnPlayer}
          isHost={isHost}
          delta={reveal.deltas.find((d) => d.playerId === selfId) ?? null}
          vote={actions.vote}
          voted={false}
          onVote={actions.castVote}
          endRequested={actions.endRequested}
          onEndTurn={actions.endTurn}
          advanced={advanced}
          onForceAdvance={() => {
            setAdvanced(true);
            forceAdvance();
          }}
        />
      }
    >
      <RevealBoard
        ranking={reveal.ranking}
        players={licensePlayers}
        selfId={selfId}
        attribution={reveal.attribution}
        startAt={reveal.startAt}
        serverOffsetMs={offsetMs}
      />
    </Screen>
  );
}
